import { Crop } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { CROP_PRESETS, isFullFrame, type CropRect } from '../model'
import { InspectorPanel } from './InspectorPanel'
import styles from '../styles/inspector.module.scss'

export interface CropInspectorProps {
  crop: CropRect | null
  /** Source frame aspect (width / height) — a ratio is a pixel ratio, and
   * turning it into the fractions this rectangle is stored in needs it. */
  frameAspect: number
  /** Key of the selected preset, not its value: "Original" and "16:9" can
   * resolve to the same number on landscape footage, and the chips must
   * still show which one was chosen. */
  preset: string
  onPickPreset: (key: string) => void
  onClear: () => void
}

/** The project's framing: ratio presets and what the crop currently is.
 * The rectangle itself is dragged on the video — see ``CropOverlay``. */
export function CropInspector({
  crop,
  frameAspect,
  preset,
  onPickPreset,
  onClear,
}: CropInspectorProps) {
  return (
    <InspectorPanel
      icon={<Crop aria-hidden />}
      title="Enquadramento"
      meta={describe(crop, frameAspect)}
    >
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Proporção</span>
        <div className={styles.chips}>
          {CROP_PRESETS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={styles.chip}
              aria-pressed={preset === option.key}
              onClick={() => {
                onPickPreset(option.key)
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.field}>
        <Button variant="ghost" onClick={onClear} disabled={isFullFrame(crop)}>
          Usar o quadro inteiro
        </Button>
      </div>
    </InspectorPanel>
  )
}

/** What the export will be, rather than a pair of fractions: how much of the
 * width is kept, and the shape it comes out as. */
function describe(crop: CropRect | null, frameAspect: number): string {
  if (isFullFrame(crop) || !crop) return 'quadro inteiro'
  const ratio = (frameAspect * crop.width) / crop.height
  return `${String(Math.round(crop.width * 100))}% · ${ratio.toFixed(2).replace('.', ',')}:1`
}
